import React, { useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import apiService from "../../services/api";

// Fallback testimonials if API fails
const fallbackTestimonials = [
  {
    name: "Maria Doe",
    designation: "Traveller",
    image: "/assets/img/testimonial/testi_1_1.jpg",
    text: "A home that perfectly blends sustainability with luxury until I discovered Ecoland Residence. From the moment I stepped into this community, I knew it was where I wanted to live. The commitment to eco-friendly living.",
    rating: 5,
  },
  {
    name: "Andrew Simon",
    designation: "Traveller",
    image: "/assets/img/testimonial/testi_1_2.jpg",
    text: "The home boasts sleek, contemporary architecture with clean lines and expansive windows, allowing natural light to flood the interiors. It incorporates passive design principles.",
    rating: 5,
  },
  {
    name: "Alex Jordan",
    designation: "Traveller",
    image: "/assets/img/testimonial/testi_1_1.jpg",
    text: "Solar panels adorn the roof, harnessing renewable energy to power the home and even feed excess electricity back into the grid. High-performance insulation and triple-glazed windows enhance energy efficiency.",
    rating: 5,
  },
];

function TestimonialOne() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiService.getTestimonials();
        if (response && response.results && response.results.length > 0) {
          // Filter for only featured and active testimonials
          const filteredTestimonials = response.results.filter(
            testimonial => testimonial.featured && testimonial.is_active
          );
          
          if (filteredTestimonials.length > 0) {
            // Map API testimonials to component expected format
            const apiTestimonials = filteredTestimonials.map(testimonial => ({
              name: testimonial.reviewer_name,
              designation: testimonial.reviewer_title || 'Traveller',
              image: testimonial.reviewer_image || '/assets/img/testimonial/testi_1_1.jpg',
              text: testimonial.testimonial_text,
              rating: testimonial.rating,
            }));
            setTestimonials(apiTestimonials);
          } else {
            // No featured testimonials available
            setTestimonials([]);
          }
        } else {
          // Don't show testimonials if none from API
          setTestimonials([]);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Don't show testimonials on error either
        setTestimonials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Calculate Swiper configuration based on slide count
  const swiperConfig = useMemo(() => {
    const slideCount = testimonials.length;
    const minSlidesForLoop = 4; // Minimum slides to enable loop mode for testimonials
    const shouldEnableLoop = slideCount >= minSlidesForLoop;
    
    // If we have fewer slides than needed for loop, duplicate them
    let slidesToShow = testimonials;
    if (!shouldEnableLoop && slideCount > 0 && slideCount < minSlidesForLoop) {
      // Duplicate slides to reach minimum for smooth infinite scroll
      const duplicatesNeeded = Math.ceil(minSlidesForLoop / slideCount);
      slidesToShow = Array(duplicatesNeeded).fill(testimonials).flat();
    }

    console.log(`TestimonialOne: ${slideCount} slides, loop: ${shouldEnableLoop || slidesToShow.length >= minSlidesForLoop}`);
    
    return {
      slides: slidesToShow,
      loop: shouldEnableLoop || slidesToShow.length >= minSlidesForLoop,
      modules: [Pagination, Navigation],
      pagination: { clickable: true },
      spaceBetween: 30,
      centeredSlides: true,
      slidesPerGroup: 1,
      speed: 1200,
      breakpoints: {
        0: { slidesPerView: 1 },
        767: { slidesPerView: 2 },
        992: { slidesPerView: 2 },
        1200: { slidesPerView: 2 },
        1400: { slidesPerView: 3 },
      }
    };
  }, [testimonials]);

  if (loading) {
    return null; // Don't show loading for testimonials
  }
  
  // Don't render anything if no testimonials
  if (!testimonials || testimonials.length === 0) {
    return null;
  }
  
  return (
    <section className="testi-area overflow-hidden space shape-mockup-wrap" id="testi-sec">
      <div className="container-fluid p-0">
        <div className="title-area mb-20 text-center">
          <span className="sub-title">Testimonial</span>
          <h2 className="sec-title">What Clients Say About Us</h2>
        </div>
        <div className="slider-area">
          <Swiper
            modules={swiperConfig.modules}
            pagination={swiperConfig.pagination}
            spaceBetween={swiperConfig.spaceBetween}
            centeredSlides={swiperConfig.centeredSlides}
            loop={swiperConfig.loop}
            slidesPerGroup={swiperConfig.slidesPerGroup}
            speed={swiperConfig.speed}
            breakpoints={swiperConfig.breakpoints}
            className="testiSlider1 has-shadow"
          >
            {swiperConfig.slides.map((item, index) => {
              // Create unique key for duplicated slides
              const uniqueKey = `${item.name || index}-${Math.floor(index / testimonials.length)}`;
              
              return (
                <SwiperSlide key={uniqueKey}>
                  <div className="testi-card">
                    <div className="testi-card_wrapper">
                      <div className="testi-card_profile">
                        <div className="testi-card_avater">
                          <img src={item.image} alt="testimonial" />
                        </div>
                        <div className="media-body">
                          <h3 className="box-title">{item.name}</h3>
                          <span className="testi-card_desig">{item.designation}</span>
                        </div>
                      </div>
                      <div className="testi-card_review">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="fa-solid fa-star" />
                        ))}
                      </div>
                    </div>
                    <p className="testi-card_text">{item.text}</p>
                    <div className="testi-card-quote">
                      <img src="/assets/img/icon/testi-quote.svg" alt="img" />
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div className="slider-pagination" />
        </div>
      </div>
      <div className="shape-mockup d-none d-xl-block" style={{bottom:"-2%", right:"0%"}}>
        <img src="/assets/img/shape/line2.png" alt="shape" />
      </div>
      <div className="shape-mockup movingX d-none d-xl-block" style={{top:"30%", left:"5%"}}>
        <img src="/assets/img/shape/shape_7.png" alt="shape" />
      </div>
    </section>
  );
}

export default TestimonialOne;
